import { notifyError } from "@/lib/notify-error";
import { useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { FileText, MapPinned, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { JobListPage, jobListDateColumn, type JobListColumn } from "@/components/layout";
import { FieldPilotDailyLogsDialog } from "@/features/field-pilot/daily-logs-dialog";
import { useProject } from "@/components/projects";
import { RowAction, RowActionsMenu } from "@/components/row-actions-menu";
import { PilotChip } from "@/components/semantic-badges";
import { Badge } from "@/components/ui/badge";
import {
  InlineEditNameCell,
  type InlineEditNameCellHandle,
} from "@/components/inline-edit-name-cell";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";

export const Route = createFileRoute("/fieldPilot/")({
  component: FieldPilotPage,
});

interface FieldPilotJobListItem {
  _id: Id<"fieldPilotJobs">;
  projectId: Id<"projects">;
  name: string;
  sourceKind: "standalone" | "takeoff_pilot";
  scheduleRevisionName?: string;
  createdAt: number;
}

export function FieldPilotPage() {
  const navigate = useAppNavigate();
  const [dailyLogsJobId, setDailyLogsJobId] = useState<Id<"fieldPilotJobs"> | null>(null);
  const { projectId } = useProject();
  const { data: jobs, isPending } = useQuery(convexQuery(api.fieldPilot.jobs.listAll, {}));
  const updateName = useMutation(api.fieldPilot.jobs.updateName);
  const removeJob = useMutation(api.fieldPilot.jobs.remove);
  const [deletingJobIds, setDeletingJobIds] = useState<Set<string>>(() => new Set());
  // Lets a row's "Rename" action open the inline editor in that row's name cell.
  const nameCellHandles = useRef(new Map<string, InlineEditNameCellHandle>());
  const visibleJobs = ((jobs ?? []) as FieldPilotJobListItem[]).filter(
    (job) => !projectId || job.projectId === projectId
  );
  const dailyLogsJob = visibleJobs.find((job) => job._id === dailyLogsJobId) ?? null;

  const handleUpdateName = async (jobId: Id<"fieldPilotJobs">, name: string) => {
    try {
      await updateName({ jobId, name });
      toast.success("Name updated");
    } catch (error) {
      notifyError(error, "Failed to update name");
      console.error(error);
      throw error;
    }
  };

  const handleDeleteJob = async (jobId: Id<"fieldPilotJobs">) => {
    setDeletingJobIds((current) => {
      const next = new Set(current);
      next.add(String(jobId));
      return next;
    });

    try {
      await removeJob({ jobId });
      toast.success("Job deleted");
    } catch (error) {
      notifyError(error, "Failed to delete job");
      console.error(error);
    } finally {
      setDeletingJobIds((current) => {
        const next = new Set(current);
        next.delete(String(jobId));
        return next;
      });
    }
  };

  const handleNewFieldPilot = () => {
    navigate({ to: "/fieldPilot/new", title: "New FieldPilot Job" });
  };

  // Built per render: `@/components/layout` and this route import each other through the
  // workspace route manifest, so module-level calls into the layout barrel can run too early.
  // FieldPilot jobs are only active or archived today, so there is no Status column. When jobs get
  // a progress status, add `{ id: "status", header: "Status", cell: (job) => <StatusBadge … /> }`
  // first, to line up with the other pilot lists.
  const columns: JobListColumn<FieldPilotJobListItem>[] = [
    {
      id: "source",
      header: "Source",
      cell: (job) =>
        job.sourceKind === "takeoff_pilot" ? (
          <PilotChip pilot="takeoff" />
        ) : (
          <Badge variant="outline" size="sm">
            Standalone
          </Badge>
        ),
    },
    {
      id: "schedule",
      header: "Schedule",
      variant: "muted",
      cell: (job) => job.scheduleRevisionName ?? "No schedule",
    },
    jobListDateColumn("Created", (job) => job.createdAt),
  ];

  return (
    <JobListPage
      title="FieldPilot"
      newAction={{ label: "New FieldPilot Job", onClick: handleNewFieldPilot }}
      emptyState={{
        icon: MapPinned,
        title: "No FieldPilot jobs yet",
        description:
          "Create one from a TakeoffPilot job or start standalone with a schedule and plan drawings.",
      }}
      isLoading={isPending}
      rows={visibleJobs}
      getRowKey={(job) => job._id}
      getRowName={(job) => job.name}
      getRowLink={(job) => ({
        to: "/fieldPilot/$jobId",
        params: { jobId: job._id },
        title: job.name,
      })}
      renderName={(job) => (
        <InlineEditNameCell
          ref={(handle) => {
            if (handle) nameCellHandles.current.set(job._id, handle);
            else nameCellHandles.current.delete(job._id);
          }}
          value={job.name}
          onSave={(name) => handleUpdateName(job._id, name)}
        />
      )}
      columns={columns}
      renderActions={(job) => (
        <FieldPilotJobActions
          job={job}
          isDeleting={deletingJobIds.has(String(job._id))}
          onRename={() => nameCellHandles.current.get(job._id)?.startEditing()}
          onViewDailyLogs={setDailyLogsJobId}
          onDelete={handleDeleteJob}
        />
      )}
    >
      <FieldPilotDailyLogsDialog
        jobId={dailyLogsJobId}
        jobName={dailyLogsJob?.name}
        open={dailyLogsJobId !== null}
        onOpenChange={(open) => !open && setDailyLogsJobId(null)}
      />
    </JobListPage>
  );
}


interface FieldPilotJobActionsProps {
  job: FieldPilotJobListItem;
  isDeleting: boolean;
  onRename: () => void;
  onViewDailyLogs: (jobId: Id<"fieldPilotJobs">) => void;
  onDelete: (jobId: Id<"fieldPilotJobs">) => Promise<void>;
}

function FieldPilotJobActions({
  job,
  isDeleting,
  onRename,
  onViewDailyLogs,
  onDelete,
}: FieldPilotJobActionsProps) {
  const canEdit = useCanEdit();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const renameRequestedRef = useRef(false);
  useOnEditAccessLost(() => setIsDeleteDialogOpen(false));

  return (
    <>
      <RowActionsMenu
        label={`Actions for ${job.name}`}
        disabled={isDeleting}
        // Start editing once the menu has closed, instead of letting it return focus to its
        // trigger, so the name field keeps focus rather than blurring (and saving) straight away.
        onCloseAutoFocus={(event) => {
          if (!renameRequestedRef.current) return;
          renameRequestedRef.current = false;
          event.preventDefault();
          onRename();
        }}
      >
        {canEdit ? (
          <RowAction
            icon={Pencil}
            onSelect={() => {
              renameRequestedRef.current = true;
            }}
          >
            Rename
          </RowAction>
        ) : null}
        <RowAction icon={FileText} onSelect={() => onViewDailyLogs(job._id)}>
          Daily logs
        </RowAction>
        {canEdit ? (
          <RowAction
            icon={Trash2}
            destructive
            disabled={isDeleting}
            onSelect={() => setIsDeleteDialogOpen(true)}
          >
            Delete
          </RowAction>
        ) : null}
      </RowActionsMenu>
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete FieldPilot Job"
        description={`Are you sure you want to delete "${job.name}"? This action cannot be undone.`}
        onConfirm={() => onDelete(job._id)}
        disabled={isDeleting}
        confirmLabel="Delete"
        confirmVariant="destructive"
      />
    </>
  );
}
